import { useState, useRef, useEffect, useCallback } from "react";
import { useMap } from "react-leaflet";
import { useLang } from "@/contexts/LanguageContext";

interface SearchResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  address?: {
    country?: string;
    city?: string;
    town?: string;
    state?: string;
  };
}

// ─── マップ移動コンポーネント（react-leaflet内部で使用）────────
function FlyToLocation({ target }: { target: { lat: number; lng: number; zoom: number } | null }) {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lng], target.zoom, { duration: 1.2 });
    }
  }, [target, map]);
  return null;
}

interface MapSearchProps {
  onFlyTo: (lat: number, lng: number, zoom: number) => void;
}

export function MapSearchBar({ onFlyTo }: MapSearchProps) {
  const { lang } = useLang();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 外クリックで閉じる
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=6&addressdetails=1&accept-language=${lang === "ja" ? "ja,en" : "en"}`,
        { headers: { "Accept-Language": lang === "ja" ? "ja,en" : "en" } }
      );
      const data: SearchResult[] = await res.json();
      setResults(data);
      setOpen(data.length > 0);
      if (data.length === 0) setError(lang === "ja" ? "見つかりませんでした" : "No results found");
    } catch {
      setError(lang === "ja" ? "検索エラー" : "Search error");
    } finally {
      setLoading(false);
    }
  }, [lang]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length >= 2) {
      debounceRef.current = setTimeout(() => search(val), 500);
    } else {
      setResults([]);
      setOpen(false);
      setError("");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      search(query);
    }
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleSelect = (result: SearchResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    // 場所の種類によってズームレベルを調整
    let zoom = 13;
    const type = result.type.toLowerCase();
    if (type === "country" || type === "administrative") zoom = 6;
    else if (type === "state" || type === "region") zoom = 8;
    else if (type === "city" || type === "town") zoom = 12;
    else if (type === "suburb" || type === "neighbourhood") zoom = 14;
    else if (type === "building" || type === "house") zoom = 17;
    onFlyTo(lat, lng, zoom);
    setQuery(result.display_name.split(",")[0]);
    setOpen(false);
  };

  const handleClear = () => {
    setQuery("");
    setResults([]);
    setOpen(false);
    setError("");
    inputRef.current?.focus();
  };

  const getPlaceIcon = (type: string) => {
    const t = type.toLowerCase();
    if (t === "country") return "ri-earth-line";
    if (t === "state" || t === "administrative") return "ri-government-line";
    if (t === "city" || t === "town") return "ri-building-2-line";
    if (t === "suburb" || t === "neighbourhood") return "ri-map-pin-2-line";
    if (t === "building" || t === "house") return "ri-home-4-line";
    if (t === "railway" || t === "station") return "ri-train-line";
    if (t === "airport") return "ri-flight-takeoff-line";
    return "ri-map-pin-line";
  };

  const getShortName = (result: SearchResult) => {
    const parts = result.display_name.split(",");
    return parts[0].trim();
  };

  const getSubName = (result: SearchResult) => {
    const parts = result.display_name.split(",");
    return parts.slice(1, 3).map((s) => s.trim()).filter(Boolean).join(", ");
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative flex items-center">
        <div className="absolute left-3 w-4 h-4 flex items-center justify-center pointer-events-none">
          {loading
            ? <i className="ri-loader-4-line text-amber-400 text-sm animate-spin"></i>
            : <i className="ri-search-line text-gray-500 text-sm"></i>
          }
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={lang === "ja" ? "場所を検索..." : "Search location..."}
          className="w-full bg-white/5 border border-white/10 text-white placeholder-gray-600 text-sm rounded-xl pl-9 pr-8 py-2.5 outline-none focus:border-amber-400/40 transition-colors"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-2.5 w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-300 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        )}
      </div>

      {/* ドロップダウン結果 */}
      {open && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#0f1015] border border-white/10 rounded-xl overflow-hidden z-[2000] shadow-2xl">
          {results.map((result) => (
            <button
              key={result.place_id}
              onClick={() => handleSelect(result)}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition-colors cursor-pointer text-left border-b border-white/5 last:border-0"
            >
              <div className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-lg flex-shrink-0">
                <i className={`${getPlaceIcon(result.type)} text-amber-400 text-sm`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium truncate">{getShortName(result)}</p>
                <p className="text-gray-500 text-xs truncate">{getSubName(result)}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* エラー表示 */}
      {error && !open && query.trim().length >= 2 && !loading && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[#0f1015] border border-white/10 rounded-xl px-3 py-2.5 z-[2000]">
          <p className="text-gray-500 text-xs">{error}</p>
        </div>
      )}
    </div>
  );
}

export { FlyToLocation };
